// =============================================================================
// src/features/explore/explore-menus.ts — Construcao dos menus/chips e painel.
//
// Proveniencia: assets/explore.js `fillMenu` (442-453), `buildLayersPanel`
//   (457-492) e os helpers de chip `setChip`/`markChecked`/`toggleActive`
//   (636-643). Extraidos para manter ExploreUI curto (arquivos < 500 linhas) e as
//   funcoes greppaveis. Classes/atributos ARIA sao paridade — NAO alterar.
//
// SRP: so DOM-building/atualizacao de menus e toggles. Sem estado do mapa.
// =============================================================================

import { qsa } from '../../app/dom';
import { CAT_META, CAMPAIGN_MARKER_META, METRIC_META, TSE_ZONE_TOGGLE_META, G26_LOCAIS_TOGGLE_META, type ExploreColorMetric } from './constants';
import type { VoteVizModeMeta, ExploreVoteViz } from './explore-vote-constants';
import type { ExplorePoiCategory, ExploreCampaignMarkerKind, HexColor } from '../../app/types';

/** Item de menu radio: valor, rotulo e (opcional) swatch de cor. */
export interface ExploreMenuItem {
  readonly value: string;
  readonly label: string;
  readonly swatch?: HexColor;
}

/** Preenche um menu radio com itens; cada clique chama onPick (assets/explore.js 442-453). */
export function fillExploreMenu(
  menu: HTMLElement,
  items: readonly ExploreMenuItem[],
  onPick: (value: string) => void,
): void {
  menu.innerHTML = '';
  items.forEach((item) => menu.appendChild(buildMenuItemButton(item, onPick)));
}

/** Cria o <button> de um item de menu (swatch + rotulo + check). */
function buildMenuItemButton(item: ExploreMenuItem, onPick: (value: string) => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'exp-menu-item';
  button.setAttribute('role', 'menuitemradio');
  button.dataset.value = item.value;
  const swatch = item.swatch ? `<span class="exp-menu-swatch" style="background:${item.swatch}"></span>` : '';
  button.innerHTML = `${swatch}<span>${item.label}</span><i class="ti ti-check exp-menu-check"></i>`;
  button.addEventListener('click', (ev) => { ev.stopPropagation(); onPick(item.value); });
  return button;
}

/** Cria a opcao de "Camadas políticas" (radio) no painel (assets/explore.js 461-467). */
export function buildPoliticalOption(metric: ExploreColorMetric, onPick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'exp-pol-opt';
  button.dataset.metric = metric;
  button.setAttribute('role', 'radio');
  button.innerHTML = `<span class="exp-pol-dot"></span><span>${METRIC_META[metric]?.label ?? metric}</span>`;
  button.addEventListener('click', onPick);
  return button;
}

/** Cria um botao do seletor segmentado "Tipo de Visualização" (icone + rotulo). */
export function buildVoteVizSegment(meta: VoteVizModeMeta, onPick: (mode: ExploreVoteViz) => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'exp-viz-btn';
  button.dataset.viz = meta.value;
  button.setAttribute('aria-pressed', 'false');
  button.innerHTML = `<i class="ti ${meta.icon}"></i><span>${meta.label}</span>`;
  button.addEventListener('click', () => onPick(meta.value));
  return button;
}

/** Rotulo/icone/cor minimo de uma linha-toggle (POI ou campanha). */
interface ToggleRowMeta {
  readonly label: string;
  readonly icon: string;
  readonly color: HexColor;
}

/** Cria a linha-toggle generica (switch ARIA) usada por POIs e campanha. */
function makeToggleRow(meta: ToggleRowMeta, onToggle: (on: boolean) => void): HTMLButtonElement {
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'exp-toggle';
  row.setAttribute('role', 'switch');
  row.setAttribute('aria-checked', 'false');
  row.innerHTML =
    `<span class="exp-toggle-ic" style="--mc:${meta.color}"><i class="ti ${meta.icon}"></i></span>` +
    `<span class="exp-toggle-label">${meta.label}</span><span class="exp-switch"></span>`;
  row.addEventListener('click', () => {
    const on = row.getAttribute('aria-checked') !== 'true';
    row.setAttribute('aria-checked', on ? 'true' : 'false');
    row.classList.toggle('is-on', on);
    onToggle(on);
  });
  return row;
}

/** Cria a linha-toggle de uma categoria de POI (assets/explore.js 472-483). */
export function buildPoiToggle(cat: ExplorePoiCategory, onToggle: (on: boolean) => void): HTMLButtonElement {
  const row = makeToggleRow(CAT_META[cat], onToggle);
  row.dataset.cat = cat;
  return row;
}

/** Cria a linha-toggle de um marcador de campanha (decisao de produto 2026-05-31). */
export function buildCampaignToggle(kind: ExploreCampaignMarkerKind, onToggle: (on: boolean) => void): HTMLButtonElement {
  const row = makeToggleRow(CAMPAIGN_MARKER_META[kind], onToggle);
  row.dataset.campaign = kind;
  return row;
}

/** Cria a linha-toggle da camada de zonas eleitorais do TSE (2026-10-13). */
export function buildTseZoneToggle(onToggle: (on: boolean) => void): HTMLButtonElement {
  const row = makeToggleRow(TSE_ZONE_TOGGLE_META, onToggle);
  row.id = 'expTseZonesToggle';
  return row;
}

/** Cria a linha-toggle da camada de locais de votacao 2026 (TSE, 2026-10-13). */
export function buildG26LocaisToggle(onToggle: (on: boolean) => void): HTMLButtonElement {
  const row = makeToggleRow(G26_LOCAIS_TOGGLE_META, onToggle);
  row.id = 'expG26LocaisToggle';
  return row;
}

/** Desfaz o estado visual de um switch (ex.: a camada falhou ao carregar). */
export function resetToggleRow(row: HTMLElement): void {
  row.setAttribute('aria-checked', 'false');
  row.classList.remove('is-on');
}

/** Atualiza o texto da .exp-chip-val de um chip (assets/explore.js setChip, 636). */
export function setChipText(chip: HTMLElement | null, text: string): void {
  const value = chip ? chip.querySelector<HTMLElement>('.exp-chip-val') : null;
  if (value) value.textContent = text;
}

/** Liga/desliga a classe .is-active de um chip (assets/explore.js toggleActive, 637). */
export function toggleChipActive(chip: HTMLElement | null, on: boolean): void {
  chip?.classList.toggle('is-active', on);
}

/** Marca o item .exp-menu-item cujo data-value casa (assets/explore.js markChecked, 638-643). */
export function markMenuChecked(menu: HTMLElement | null, value: string): void {
  if (!menu) return;
  qsa<HTMLElement>('.exp-menu-item', menu).forEach((button) => {
    const on = button.dataset.value === value;
    button.classList.toggle('is-checked', on);
    button.setAttribute('aria-checked', on ? 'true' : 'false');
  });
}
